import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  History, ChevronDown, ChevronRight, Play, Loader2, AlertCircle,
  CheckCircle2, XCircle, Clock, RefreshCw,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import { formatNumber, formatRelative } from './format';

const STATUS_META = {
  draft:     { icon: Clock,        variant: 'muted',   label: 'Draft'     },
  pending:   { icon: Clock,        variant: 'muted',   label: 'Pending'   },
  queued:    { icon: Clock,        variant: 'muted',   label: 'Queued'    },
  running:   { icon: Loader2,      variant: 'primary', label: 'Running'   },
  completed: { icon: CheckCircle2, variant: 'success', label: 'Completed' },
  succeeded: { icon: CheckCircle2, variant: 'success', label: 'Succeeded' },
  failed:    { icon: XCircle,      variant: 'error',   label: 'Failed'    },
  cancelled: { icon: XCircle,      variant: 'muted',   label: 'Cancelled' },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { icon: Clock, variant: 'muted', label: status || '—' };
  const Icon = meta.icon;
  return (
    <span className={`bb-pill bb-pill--${meta.variant}`}>
      <Icon size={11} className={status === 'running' ? 'bb-spin' : ''} />
      {meta.label}
    </span>
  );
}

function CampaignRunDetail({ campaignId }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['backlog-buster', 'campaign-detail', campaignId],
    queryFn: () => apiClient.get(`/api/backlog-buster/campaigns/${campaignId}`),
    staleTime: 30_000,
  });

  if (isLoading) {
    return <div className="bb-run-detail bb-run-detail--loading"><Loader2 size={14} className="bb-spin" /> Loading runs…</div>;
  }
  if (error) {
    return <div className="bb-run-detail bb-run-detail--error"><AlertCircle size={13} /> {error.message}</div>;
  }

  const runs = data?.runs || data?.campaign?.runs || [];
  if (runs.length === 0) {
    return <div className="bb-run-detail bb-run-detail--empty">No runs recorded yet.</div>;
  }

  return (
    <div className="bb-run-detail">
      <table className="bb-runs">
        <thead>
          <tr>
            <th>Tier</th>
            <th>Processed</th>
            <th>Classified</th>
            <th>Avg confidence</th>
            <th>Cost</th>
            <th>Started</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => {
            const conf = r.avg_confidence ?? r.confidence_distribution?.mean;
            return (
              <tr key={r.run_id || r.id}>
                <td data-label="Tier">
                  <span className="bb-mono">{r.tier || r.method || '—'}</span>
                </td>
                <td data-label="Processed">{formatNumber(r.items_processed ?? r.processed)}</td>
                <td data-label="Classified">{formatNumber(r.items_classified ?? r.classified)}</td>
                <td data-label="Avg confidence">
                  {conf != null ? `${(Number(conf) * 100).toFixed(1)}%` : '—'}
                </td>
                <td data-label="Cost">
                  {r.cost_usd != null ? `$${Number(r.cost_usd).toFixed(4)}` : '—'}
                </td>
                <td data-label="Started">{formatRelative(r.started_at || r.created_at)}</td>
                <td data-label="Status"><StatusPill status={r.status} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CampaignRow({ campaign, expanded, onToggle, onRun, runBusy }) {
  const status = campaign.status || 'draft';
  const runnable = status === 'draft' || status === 'pending';

  return (
    <>
      <tr
        className={`bb-row ${expanded ? 'is-expanded' : ''}`}
        onClick={onToggle}
      >
        <td data-label="" className="bb-row__caret">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td data-label="Name" className="bb-row__name">
          <strong>{campaign.name}</strong>
          {campaign.dept_code && (
            <span className="bb-row__sub">dept: {campaign.dept_code}</span>
          )}
        </td>
        <td data-label="Status"><StatusPill status={status} /></td>
        <td data-label="Items">
          {formatNumber(campaign.items_processed)}
          <span className="bb-row__sub">processed</span>
        </td>
        <td data-label="Classified">
          {formatNumber(campaign.items_classified)}
          <span className="bb-row__sub">classified</span>
        </td>
        <td data-label="Cost">
          {campaign.cost_usd != null ? `$${Number(campaign.cost_usd).toFixed(2)}` : '—'}
        </td>
        <td data-label="Created">{formatRelative(campaign.created_at)}</td>
        <td data-label="" className="bb-row__action">
          {runnable && (
            <button
              type="button"
              className="bb-btn bb-btn--ghost bb-btn--sm"
              onClick={(e) => { e.stopPropagation(); onRun(campaign.id); }}
              disabled={runBusy}
              title="Start this campaign"
            >
              {runBusy ? <Loader2 size={12} className="bb-spin" /> : <Play size={12} />}
              Run
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bb-row__expansion">
          <td colSpan={8}>
            <CampaignRunDetail campaignId={campaign.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function CampaignHistory() {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState(null);

  const list = useQuery({
    queryKey: ['backlog-buster', 'campaigns'],
    queryFn: () => apiClient.get('/api/backlog-buster/campaigns'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const runMutation = useMutation({
    mutationFn: (id) => apiClient.post(`/api/backlog-buster/campaigns/${id}/run`, {}),
    onSuccess: (_data, id) => {
      logger.ui.info?.('BacklogBuster', 'campaign_run_started', { id });
      queryClient.invalidateQueries({ queryKey: ['backlog-buster', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['backlog-buster', 'campaign-detail', id] });
    },
    onError: (err, id) => {
      logger.ui.error?.('BacklogBuster', 'campaign_run_failed', { id, message: err?.message });
      alert(`Failed to start campaign: ${err?.message}`);
    },
  });

  const campaigns = list.data?.campaigns || list.data || [];

  return (
    <section className="bb-section">
      <header className="bb-section__head">
        <div className="bb-section__title">
          <History size={16} /> Campaign History
        </div>
        <button
          type="button"
          className="bb-btn bb-btn--ghost"
          onClick={() => list.refetch()}
          disabled={list.isFetching}
        >
          <RefreshCw size={14} className={list.isFetching ? 'bb-spin' : ''} />
          Refresh
        </button>
      </header>

      {list.isLoading && (
        <div className="bb-state"><Loader2 size={18} className="bb-spin" /> Loading campaigns…</div>
      )}
      {list.error && (
        <div className="bb-state bb-state--error">
          <AlertCircle size={16} /> Failed to load: {list.error.message}
        </div>
      )}
      {!list.isLoading && !list.error && campaigns.length === 0 && (
        <div className="bb-state bb-state--empty">
          No campaigns yet — build one above and click <strong>Run</strong>.
        </div>
      )}

      {campaigns.length > 0 && (
        <div className="bb-table-wrap">
          <table className="bb-table">
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th>Status</th>
                <th>Processed</th>
                <th>Classified</th>
                <th>Cost</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <CampaignRow
                  key={c.id}
                  campaign={c}
                  expanded={expandedId === c.id}
                  onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                  onRun={(id) => runMutation.mutate(id)}
                  runBusy={runMutation.isPending && runMutation.variables === c.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default CampaignHistory;
